<template>
  <div class="quizform" v-if="!quizStarted">
    <!-- Difficulty Dropdown -->
    <label class="form-label">Select difficulty</label>
    <select
      v-model="quizDifficulty"
      class="form-select"
      aria-label="Select difficulty"
    >
      <option value="easy">Easy</option>
      <option value="medium">Medium</option>
      <option value="hard">Hard</option>
    </select>

    <!-- Questions Range -->
    <label for="customRange1" style="margin-top: 1rem" class="form-label"
      >Number of Questions: {{ numberOfQuestions }}</label
    >
    <input
      type="range"
      class="form-range"
      min="1"
      max="50"
      step="1"
      id="customRange1"
      v-model="numberOfQuestions"
    />

    <!-- Next Button -->
    <button @click="sendForm" style="margin-top: 1rem" class="next-btn">
      Next →
    </button>
  </div>

  <!-- Quiz Container -->
  <div v-if="quizStarted">
    <div class="quiz-container" v-if="questions.length > 0">
      <h2 class="question">{{ currentQuestion.question }}</h2>
      <div class="options-grid">
        <button
          v-for="option in shuffledOptions"
          :key="option"
          :class="[
            'option-btn',
            {
              selected: option === selectedOption,
              correct:
                showFeedback && option === currentQuestion.correct_answer,
              wrong:
                showFeedback &&
                option === selectedOption &&
                option !== currentQuestion.correct_answer,
            },
          ]"
          @click="selectOption(option)"
          :disabled="showFeedback"
        >
          {{ option }}
        </button>
      </div>
      <div v-if="!showFeedback && selectedOption" class="submit-container">
        <button @click="submitAnswer" class="submit-btn">Submit</button>
      </div>
      <div v-if="showFeedback" class="next-container">
        <button @click="nextQuestion" class="next-btn">Next →</button>
      </div>
    </div>

    <!-- Quiz Placeholder -->
    <div class="quiz-container" v-if="!questions.length > 0">
      <h2 class="question placeholder"></h2>
      <div class="options-grid">
        <button class="option-btn placeholder"></button>
        <button class="option-btn placeholder"></button>
        <button class="option-btn placeholder"></button>
        <button class="option-btn placeholder"></button>
      </div>
    </div>
  </div>

  <!-- Result Modal -->
  <div v-if="showResult" class="modal-overlay">
    <div id="result-modal">
      <h2>Quiz Completed!</h2>
      <p>You got {{ correctAnswers }} out of {{ questions.length }} correct!</p>

      <div style="display: flex; justify-content: space-around">
        <router-link class="router-link" to="/">
          <button type="button" class="reset-btn">Start Menu</button>
        </router-link>

        <button @click="resetQuiz" class="reset-btn">Restart Quiz</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "QuizPage",
  data() {
    return {
      questions: [], // Store questions from API
      currentQuestionIndex: 0,
      shuffledOptions: [], // Shuffled options (correct + incorrect)
      selectedOption: null,

      showFeedback: false,
      correctAnswers: 0,
      showResult: false,

      numberOfQuestions: 10,
      quizDifficulty: "easy",
      quizStarted: false,
    };
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
  },
  methods: {
    async fetchQuestions() {
      var difficulty = this.quizDifficulty;

      // Get questions from API
      const response = await fetch(
        `https://opentdb.com/api.php?amount=${this.numberOfQuestions}&category=9&difficulty=${difficulty}&type=multiple`
      );

      const data = await response.json();

      // this.questions = data.results;

      this.questions = data.results.map((question) => {
        return {
          ...question,
          question: this.decodeHtml(question.question),
          correct_answer: this.decodeHtml(question.correct_answer),
          incorrect_answers: question.incorrect_answers.map((answer) =>
            this.decodeHtml(answer)
          ),
        };
      });

      this.shuffleOptions();
    },

    decodeHtml(html) {
      var txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
    },

    shuffleOptions() {
      const options = [
        this.currentQuestion.correct_answer,
        ...this.currentQuestion.incorrect_answers,
      ];
      this.shuffledOptions = options.sort(() => Math.random() - 0.5);
    },

    selectOption(option) {
      // Store selected option
      this.selectedOption = option;
    },

    submitAnswer() {
      // Show feedback
      this.showFeedback = true;

      // Check if selected option is correct
      if (this.selectedOption === this.currentQuestion.correct_answer) {
        this.correctAnswers++; // Increment correct answer count
      }
    },

    nextQuestion() {
      // Move to the next question or show result if quiz is completed
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        this.selectedOption = null; // Clear the selected option for the new question
        this.showFeedback = false; // Hide feedback until submit is pressed
        this.shuffleOptions();
      } else {
        console.log("Quiz finished, showing result modal.");
        this.showResult = true;
      }
    },

    resetQuiz() {
      // Reset the quiz to start over
      this.currentQuestionIndex = 0;
      this.correctAnswers = 0;
      this.selectedOption = null;
      this.showFeedback = false;
      this.showResult = false;

      this.quizStarted = false;
    },

    sendForm() {
      this.quizStarted = true;

      this.fetchQuestions();
    },
  },
  mounted() {},
};
</script>

<style scoped>
.quiz-container {
  max-width: 75%;
  min-width: 22rem;
  margin: 20px auto;
  padding: 20px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
}

.quizform {
  width: 90%;
  min-width: 20rem;
  margin: 20px auto;
  padding: 20px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
}

.question {
  font-size: 24px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: normal;

  width: 100%;
  align-self: center;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.option-btn {
  padding: 15px 10px;
  font-size: 16px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-align: center;
  line-height: 1.2;
  transition: background-color 0.3s;
}

.option-btn.selected {
  background-color: #007bff;
}

.option-btn.correct {
  background-color: #28a745;
}

.option-btn.wrong {
  background-color: #dc3545;
}

.option-btn:hover {
  background-color: #555;
}

.submit-container {
  margin-top: 20px;
  text-align: center;
}

.submit-btn {
  padding: 10px 20px;
  font-size: 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.submit-btn:hover {
  background-color: #0056b3;
}

.next-container {
  margin-top: 20px;
  text-align: center;
}

.next-btn {
  padding: 10px 20px;
  font-size: 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.next-btn:hover {
  background-color: #0056b3;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

#result-modal {
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

.reset-btn {
  padding: 8px 12px;
  font-size: 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.reset-btn:hover {
  background-color: #0056b3;
}
</style>
